// Minimal fetch wrapper. Attaches the access token from local auth state
// (web flow) and parses JSON envelopes uniformly.

import { getAccessToken } from './auth';

export type ApiError = {
  status: number;
  code: string;
  message: string;
};

export class ApiException extends Error {
  status: number;
  code: string;
  constructor(e: ApiError) {
    super(e.message);
    this.status = e.status;
    this.code = e.code;
  }
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const access = getAccessToken();
  if (access) headers.Authorization = `Bearer ${access}`;

  const resp = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });

  if (resp.status === 204) return undefined as T;

  const ct = resp.headers.get('content-type') ?? '';
  const body = ct.includes('application/json') ? await resp.json() : undefined;

  if (!resp.ok) {
    const err = body?.error ?? { code: 'unknown', message: resp.statusText };
    throw new ApiException({ status: resp.status, code: err.code, message: err.message });
  }
  return body as T;
}
